// src/data/invitesHelpers.js
//
// Ticket 16: invite flow data layer.
//
// Invites live at `invites/{orgId}_{emailHash}` per data-model doc §2.5.
// The doc id is deterministic so a re-invite for the same (org, email)
// pair collides with the existing pending invite instead of creating
// duplicates. The email is truncated-hashed (sha256, first 12 hex
// chars) to avoid leaking the invited email through doc paths.
//
// Phase-1 compromise: invites are NOT emailed to the invited user.
// The org admin shares the fact of the invite out-of-band (Slack,
// email, etc.). On the invited user's next login, the SessionProvider
// discovers pending invites matching their email and surfaces a
// prompt (see src/auth/sessionContext.js and src/components/
// InvitePromptBanner.js).

import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { ensureMembership } from '../auth/membershipHelpers';

const INVITES = 'invites';

/**
 * Hashes an email via the Web Crypto API and returns the first 12
 * hex chars. Used in the deterministic invite doc id.
 */
export async function emailHash(email) {
  const normalized = (email || '').toLowerCase().trim();
  const encoder = new TextEncoder();
  const buffer = encoder.encode(normalized);
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < 6; i += 1) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

/**
 * Returns the deterministic doc id for a given (org, email) pair.
 */
export async function inviteDocId(orgId, email) {
  const h = await emailHash(email);
  return `${orgId}_${h}`;
}

/**
 * Create a pending invite. Returns the doc id. Admin-only; rules
 * enforce isOrgAdmin(orgId).
 */
export async function createInvite({ orgId, email, role, invitedBy }) {
  if (!orgId || !email || !role || !invitedBy) {
    throw new Error('createInvite requires orgId, email, role, invitedBy');
  }
  if (role !== 'orgAdmin' && role !== 'reviewer') {
    throw new Error(`createInvite: role must be orgAdmin|reviewer (got ${role})`);
  }
  const normalizedEmail = email.toLowerCase().trim();
  const eHash = await emailHash(normalizedEmail);
  const id = `${orgId}_${eHash}`;
  const ref = doc(db, INVITES, id);
  await setDoc(ref, {
    orgId,
    email: normalizedEmail,
    emailHash: eHash,
    role,
    invitedBy,
    createdAt: serverTimestamp(),
    status: 'pending',
  });
  return id;
}

/**
 * List pending invites for a given org. Used by ticket 17's
 * member-management UI.
 */
export async function listPendingInvitesForOrg(orgId) {
  if (!orgId) return [];
  const q = query(
    collection(db, INVITES),
    where('orgId', '==', orgId),
    where('status', '==', 'pending'),
  );
  const snap = await getDocs(q);
  const out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  out.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
  return out;
}

/**
 * List pending invites addressed to a given email. Called by the
 * session provider on login to surface the invite prompt.
 */
export async function listPendingInvitesForEmail(email) {
  const normalized = (email || '').toLowerCase().trim();
  if (!normalized) return [];
  const q = query(
    collection(db, INVITES),
    where('email', '==', normalized),
    where('status', '==', 'pending'),
  );
  const snap = await getDocs(q);
  const out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  return out;
}

/**
 * Accept an invite. Two writes:
 *   1. ensureMembership (org-scoped) with the invited role + identity
 *   2. Update the invite doc: status='accepted', acceptedAt, acceptedBy
 *
 * Rules gate the membership create on the invite existing and matching
 * the caller's email, and gate the invite update on the caller being
 * the invited email.
 */
export async function acceptInvite(invite, { uid, email, displayName }) {
  if (!invite || !invite.id) throw new Error('acceptInvite requires the invite object');
  if (!uid || !email) throw new Error('acceptInvite requires uid + email');
  await ensureMembership({
    uid,
    orgId: invite.orgId,
    role: invite.role,
    email,
    displayName: displayName || '',
    createdBy: 'invite-accept',
    inviteId: invite.id,
  });
  await setDoc(
    doc(db, INVITES, invite.id),
    {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      acceptedBy: uid,
    },
    { merge: true },
  );
}

/**
 * Revoke a pending invite. Only the inviting org admin (rules-gated).
 */
export async function revokeInvite(inviteId) {
  if (!inviteId) throw new Error('revokeInvite requires inviteId');
  await setDoc(
    doc(db, INVITES, inviteId),
    {
      status: 'revoked',
    },
    { merge: true },
  );
}
